import { useState, useEffect, useRef } from 'react'
import API from '../../services/apiService'
import { Skeleton } from '../Skeleton/Skeleton'
import { useConfirm } from '../ConfirmModal/ConfirmModal'
import './ResourcesManager.css'

// ── SVG Icons ──────────────────────────────────────────────────────────

const ResourceIcon = () => (
  <svg viewBox="0 0 24 24" width="40" height="40">
    <defs>
      <linearGradient id="resourceGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#fef3c7" />
        <stop offset="50%" stopColor="#fbbf24" />
        <stop offset="100%" stopColor="#d97706" />
      </linearGradient>
      <filter id="resourceGlow">
        <feGaussianBlur stdDeviation="1.2" />
        <feComponentTransfer>
          <feFuncA type="linear" slope="1.5"/>
        </feComponentTransfer>
      </filter>
    </defs>
    <rect x="4" y="3" width="16" height="18" rx="2" fill="url(#resourceGrad)" stroke="#92400e" strokeWidth="0.8" />
    <rect x="7" y="7" width="10" height="2" rx="0.5" fill="#78350f" opacity="0.6"/>
    <rect x="7" y="11" width="7" height="1.5" rx="0.3" fill="#78350f" opacity="0.4"/>
    <rect x="7" y="14" width="8" height="1.5" rx="0.3" fill="#78350f" opacity="0.4"/>
    <rect x="7" y="17" width="6" height="1.5" rx="0.3" fill="#78350f" opacity="0.4"/>
  </svg>
)

const UploadIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16">
    <defs>
      <linearGradient id="uploadGr" x1="0%" y1="100%" x2="0%" y2="0%">
        <stop offset="0%" stopColor="#86efac"/>
        <stop offset="100%" stopColor="#22c55e"/>
      </linearGradient>
    </defs>
    <path d="M12 16V4M12 4L8 8M12 4L16 8" stroke="url(#uploadGr)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <path d="M4 14v4a2 2 0 002 2h12a2 2 0 002-2v-4" stroke="url(#uploadGr)" strokeWidth="2" strokeLinecap="round" fill="none"/>
  </svg>
)

const DeleteIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16">
    <defs>
      <linearGradient id="delGr" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#fca5a5"/>
        <stop offset="100%" stopColor="#ef4444"/>
      </linearGradient>
    </defs>
    <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" stroke="url(#delGr)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <line x1="10" y1="11" x2="10" y2="17" stroke="url(#delGr)" strokeWidth="2" strokeLinecap="round"/>
    <line x1="14" y1="11" x2="14" y2="17" stroke="url(#delGr)" strokeWidth="2" strokeLinecap="round"/>
  </svg>
)

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16">
    <defs>
      <linearGradient id="downloadGr" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#60a5fa"/>
        <stop offset="100%" stopColor="#3b82f6"/>
      </linearGradient>
    </defs>
    <path d="M12 4v12M12 16l-4-4M12 16l4-4" stroke="url(#downloadGr)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <path d="M4 14v4a2 2 0 002 2h12a2 2 0 002-2v-4" stroke="url(#downloadGr)" strokeWidth="2" strokeLinecap="round" fill="none"/>
  </svg>
)

const RefreshIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16">
    <defs>
      <linearGradient id="refreshGr" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#c4b5fd"/>
        <stop offset="100%" stopColor="#8b5cf6"/>
      </linearGradient>
    </defs>
    <path d="M1 4v6h6M23 20v-6h-6" stroke="url(#refreshGr)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <path d="M20.49 9A9 9 0 005.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 013.51 15" stroke="url(#refreshGr)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
)

// Category descriptions
const CATEGORIES = {
  java: {
    dir: 'java',
    extensions: ['.jar'],
    description: 'Java archives (Client)',
    icon: '☕'
  },
  lib: {
    dir: 'lib',
    extensions: ['.dll'],
    description: 'Library DLLs (Bot)',
    icon: '📚'
  },
  x32: {
    dir: 'x32',
    extensions: ['.dll'],
    description: 'x32 DLLs (Client)',
    icon: '🔷'
  },
  x64: {
    dir: 'x64',
    extensions: ['.dll'],
    description: 'x64 DLLs (Client)',
    icon: '🔶'
  },
  audio: {
    dir: 'audio',
    extensions: ['.wav', '.mp3', '.ogg'],
    description: 'Audio files (Bot)',
    icon: '🔊'
  },
  ui: {
    dir: 'ui',
    extensions: ['.xml'],
    description: 'UI configuration files (Client)',
    icon: '🖼️'
  },
  packs: {
    dir: 'packs',
    extensions: ['.dat', '.bin'],
    description: 'Resource packs (Client)',
    icon: '📦'
  }
}

const ResourcesManager = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  
  const [manifest, setManifest] = useState(null)
  
  // Upload state - arquivos por categoria
  const [filesByCategory, setFilesByCategory] = useState({
    java: [],
    lib: [],
    x32: [],
    x64: [],
    audio: [],
    ui: [],
    packs: []
  })
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadResults, setUploadResults] = useState(null)
  
  const [activeTab, setActiveTab] = useState('files') // 'upload', 'files' or 'map'
  
  // Map tab state
  const [mapStatus, setMapStatus] = useState(null)
  const [mapLoading, setMapLoading] = useState(false)
  const [mapFile, setMapFile] = useState(null)
  const [mapUploading, setMapUploading] = useState(false)
  const [mapDownloading, setMapDownloading] = useState(false)
  const mapFileInputRef = useRef(null)

  const confirm = useConfirm()
  const [dragOverCategory, setDragOverCategory] = useState({})
  const [mapDragOver, setMapDragOver] = useState(false)

  const [expandedCategories, setExpandedCategories] = useState({
    java: false,
    lib: false,
    x32: false,
    x64: false,
    audio: false,
    ui: false,
    packs: false
  })
  
  const fileInputRefs = useRef({})

  useEffect(() => {
    loadManifest()
  }, [])

  const loadMapStatus = async () => {
    setMapLoading(true)
    try {
      const res = await API.Resources.getMapStatus()
      if (res.success) {
        setMapStatus(res.data)
      } else {
        setError(res.error?.message || 'Failed to load map status')
      }
    } catch (err) {
      console.error('Failed to load map status:', err)
      setError('Failed to load map status')
    } finally {
      setMapLoading(false)
    }
  }

  const handleMapTabOpen = () => {
    setActiveTab('map')
    loadMapStatus()
  }

  const handleMapFileSelect = (e) => {
    const f = e.target.files?.[0]
    if (f) setMapFile(f)
  }

  const handleMapUpload = async () => {
    if (!mapFile) return
    if (!await confirm(`Upload "${mapFile.name}" as map.bin? This will replace the current map.`)) return
    setMapUploading(true)
    setError(null)
    setSuccess(null)
    try {
      const res = await API.Resources.uploadMap(mapFile)
      if (res.success) {
        setSuccess(`Map uploaded successfully! (${res.data.sizeFormatted})`)
        setMapFile(null)
        if (mapFileInputRef.current) mapFileInputRef.current.value = ''
        await loadMapStatus()
      } else {
        setError(res.error?.message || 'Failed to upload map')
      }
    } catch (err) {
      console.error('Map upload error:', err)
      setError('Failed to upload map')
    } finally {
      setMapUploading(false)
    }
  }

  const handleMapDownload = async () => {
    setMapDownloading(true)
    setError(null)
    setSuccess(null)
    try {
      await API.Resources.downloadMap()
      setSuccess('Map download started!')
    } catch (err) {
      console.error('Map download error:', err)
      setError('Failed to download map')
    } finally {
      setMapDownloading(false)
    }
  }

  // ── Drag & Drop Handlers ─────────────────────────────────────────

  const handleCategoryDragOver = (e, categoryKey) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOverCategory(prev => ({ ...prev, [categoryKey]: true }))
  }

  const handleCategoryDragLeave = (e, categoryKey) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOverCategory(prev => ({ ...prev, [categoryKey]: false }))
  }

  const handleCategoryDrop = (e, categoryKey) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOverCategory(prev => ({ ...prev, [categoryKey]: false }))

    const categoryInfo = CATEGORIES[categoryKey]
    const droppedFiles = Array.from(e.dataTransfer.files)
    const validFiles = droppedFiles.filter(f =>
      categoryInfo.extensions.some(ext => f.name.toLowerCase().endsWith(ext))
    )
    const invalidFiles = droppedFiles.filter(f => !validFiles.includes(f))

    if (invalidFiles.length > 0) {
      setError(`${invalidFiles.length} file(s) rejected: ${categoryInfo.description} only accepts ${categoryInfo.extensions.join(', ')}`)
    }

    if (validFiles.length > 0) {
      const syntheticEvent = { target: { files: validFiles.map(f => f) } }
      handleFileSelection(categoryKey, syntheticEvent)
    }
  }

  const handleMapDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setMapDragOver(true)
  }

  const handleMapDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setMapDragOver(false)
  }

  const handleMapDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setMapDragOver(false)

    const file = e.dataTransfer.files?.[0]
    if (file) {
      if (file.name.toLowerCase().endsWith('.bin')) {
        setMapFile(file)
      } else {
        setError('Only .bin files are allowed for map upload')
      }
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  const formatHash = (hash) => {
    if (!hash) return 'N/A'
    return hash.substring(0, 8) + '...'
  }

  const normalizeManifest = (data) => {
    const manifestData = data?.manifest || data || {}
    const categoryFiles = Object.values(manifestData)
    const totalFiles = data?.totalFiles ?? categoryFiles.reduce((sum, files) => sum + (files?.length || 0), 0)
    const totalSize = data?.totalSize ?? categoryFiles.flat().reduce((sum, file) => sum + (file?.size || 0), 0)
    const totalSizeFormatted = data?.totalSizeFormatted ?? formatFileSize(totalSize)

    return {
      manifest: manifestData,
      totalFiles,
      totalSize,
      totalSizeFormatted
    }
  }

  const loadManifest = async () => {
    setLoading(true)
    try {
      const response = await API.Resources.getManifest()
      if (response.success) {
        setManifest(normalizeManifest(response.data))
      } else {
        setError(response.error?.message || 'Failed to load manifest')
      }
    } catch (err) {
      console.error('Failed to load manifest:', err)
      setError('Failed to load manifest')
    } finally {
      setLoading(false)
    }
  }

  const getFilesMap = () => manifest?.manifest || {}

  const handleFileSelection = (category, event) => {
    const selectedFiles = Array.from(event.target.files)
    
    setFilesByCategory(prev => ({
      ...prev,
      [category]: [...prev[category], ...selectedFiles]
    }))
  }
  
  const handleRemoveFile = (category, fileIndex) => {
    setFilesByCategory(prev => ({
      ...prev,
      [category]: prev[category].filter((_, index) => index !== fileIndex)
    }))
  }
  
  const handleClearCategory = (category) => {
    setFilesByCategory(prev => ({
      ...prev,
      [category]: []
    }))
    
    // Clear file input
    if (fileInputRefs.current[category]) {
      fileInputRefs.current[category].value = ''
    }
  }
  
  const getTotalFilesCount = () => {
    return Object.values(filesByCategory).reduce((sum, files) => sum + files.length, 0)
  }
  
  const handleShowConfirmation = () => {
    const totalFiles = getTotalFilesCount()
    
    if (totalFiles === 0) {
      setError('Please select at least one file to upload')
      return
    }
    
    setShowConfirmation(true)
    setError(null)
  }
  
  const handleCancelUpload = () => {
    setShowConfirmation(false)
  }
  
  const handleConfirmUpload = async () => {
    setShowConfirmation(false)
    setUploading(true)
    setError(null)
    setSuccess(null)
    setUploadResults(null)
    
    try {
      // Filtrar apenas categorias com arquivos
      const filesToUpload = {}
      Object.entries(filesByCategory).forEach(([category, files]) => {
        if (files.length > 0) {
          filesToUpload[category] = files
        }
      })
      
      const response = await API.Resources.uploadFiles(filesToUpload)
      
      if (response.success) {
        const { successful, failed, total, byCategory } = response.data
        
        setUploadResults({ successful, failed, total, byCategory })
        
        if (failed.length === 0) {
          setSuccess(`All ${successful.length} file(s) uploaded successfully!`)
        } else if (successful.length === 0) {
          setError(`All ${failed.length} file(s) failed to upload`)
        } else {
          setSuccess(`${successful.length} file(s) uploaded, ${failed.length} failed`)
        }
        
        // Limpar arquivos selecionados
        setFilesByCategory({
          java: [],
          lib: [],
          x32: [],
          x64: [],
          audio: [],
          ui: [],
          packs: []
        })
        
        // Limpar inputs
        Object.keys(fileInputRefs.current).forEach(key => {
          if (fileInputRefs.current[key]) {
            fileInputRefs.current[key].value = ''
          }
        })
        
        await loadManifest()
      } else {
        setError(response.error?.message || 'Failed to upload files')
      }
    } catch (err) {
      console.error('Upload error:', err)
      setError('Failed to upload files')
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (category, filename) => {
    if (!await confirm(`Are you sure you want to delete "${filename}" from ${category}?`)) {
      return
    }
    
    setError(null)
    setSuccess(null)
    
    try {
      const response = await API.Resources.deleteFile(category, filename)
      
      if (response.success) {
        setSuccess(`File "${filename}" deleted successfully!`)
        await loadManifest()
      } else {
        setError(response.error?.message || 'Failed to delete file')
      }
    } catch (err) {
      console.error('Delete error:', err)
      setError('Failed to delete file')
    }
  }

  const handleDeleteCategory = async (category) => {
    const categoryFiles = getFilesMap()[category] || []
    const count = categoryFiles.length
    
    if (count === 0) {
      setError('No files to delete in this category')
      return
    }
    
    if (!await confirm(`⚠️ DELETE ALL FILES?\n\nThis will permanently delete ALL ${count} file(s) from ${category.toUpperCase()}.\n\nThis action cannot be undone!`, 'Delete All Files')) {
      return
    }
    
    setError(null)
    setSuccess(null)
    
    try {
      const response = await API.Resources.deleteCategory(category)
      
      if (response.success) {
        setSuccess(`Successfully deleted ${response.data.deletedCount} file(s) from ${category}!`)
        await loadManifest()
      } else {
        setError(response.error?.message || 'Failed to delete category files')
      }
    } catch (err) {
      console.error('Delete category error:', err)
      setError('Failed to delete category files')
    }
  }

  const handleSyncFilesystem = async () => {
    if (!await confirm('Sync filesystem to database? This will scan all files and update the database.')) {
      return
    }
    
    setLoading(true)
    setError(null)
    setSuccess(null)
    
    try {
      const response = await API.Resources.syncFilesystem()
      
      if (response.success) {
        setSuccess('Filesystem synced successfully!')
        setManifest(normalizeManifest(response.data))
      } else {
        setError(response.error?.message || 'Failed to sync filesystem')
      }
    } catch (err) {
      console.error('Sync error:', err)
      setError('Failed to sync filesystem')
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadAll = async () => {
    setError(null)
    setSuccess(null)
    
    try {
      setSuccess('Preparing download...')
      await API.Resources.downloadResources(getFilesMap())
      setSuccess('Download started!')
    } catch (err) {
      console.error('Download error:', err)
      setError('Failed to download resources')
    }
  }

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }))
  }

  return (
    <div className="resources-manager">
      {/* Compact Header */}
      <div className="resources-header">
        <div className="resources-header-title">
          <ResourceIcon />
          <h3>Resources</h3>
          {manifest && (
            <span className="resources-badge">{manifest.totalFiles} files • {manifest.totalSizeFormatted}</span>
          )}
        </div>
        <div className="resources-header-actions">
          <button onClick={handleSyncFilesystem} className="resources-btn-icon" disabled={loading} title="Sync">
            <RefreshIcon />
          </button>
          <button onClick={handleDownloadAll} className="resources-btn-icon" disabled={loading} title="Download All">
            <DownloadIcon />
          </button>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="resources-tabs">
        <button 
          className={`resources-tab ${activeTab === 'files' ? 'active' : ''}`}
          onClick={() => setActiveTab('files')}
        >
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path d="M3 4h18v2H3V4zm0 7h18v2H3v-2zm0 7h18v2H3v-2z" fill="currentColor"/>
          </svg>
          Manage Files
        </button>
        
        <button 
          className={`resources-tab ${activeTab === 'upload' ? 'active' : ''}`}
          onClick={() => setActiveTab('upload')}
        >
          <UploadIcon />
          Upload Files
        </button>

        <button
          className={`resources-tab ${activeTab === 'map' ? 'active' : ''}`}
          onClick={handleMapTabOpen}
        >
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path d="M3 7l9-4 9 4v10l-9 4-9-4V7z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
            <path d="M12 3v18M3 7l9 4 9-4" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinejoin="round"/>
          </svg>
          Map
        </button>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="resources-message resources-message--error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}
      
      {success && (
        <div className="resources-message resources-message--success">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)}>×</button>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'upload' && (
        <div className="resources-tab-content">
          <div className="resources-upload-grid">
            {Object.entries(CATEGORIES).map(([categoryKey, categoryInfo]) => {
              const categoryFiles = filesByCategory[categoryKey] || []
              
              return (
                <div
                  key={categoryKey}
                  className={`resources-upload-card ${dragOverCategory[categoryKey] ? 'resources-upload-card--dragover' : ''}`}
                  onClick={() => fileInputRefs.current[categoryKey]?.click()}
                  onDragOver={(e) => handleCategoryDragOver(e, categoryKey)}
                  onDragLeave={(e) => handleCategoryDragLeave(e, categoryKey)}
                  onDrop={(e) => handleCategoryDrop(e, categoryKey)}
                >
                  <div className="resources-upload-card-header">
                    <span className="resources-card-icon">{categoryInfo.icon}</span>
                    <div className="resources-card-info">
                      <h4>{categoryInfo.description}</h4>
                      <span className="resources-card-ext">{categoryInfo.extensions.join(', ')}</span>
                    </div>
                    {categoryFiles.length > 0 && (
                      <span className="resources-card-badge">{categoryFiles.length}</span>
                    )}
                  </div>
                  
                  <input
                    ref={el => fileInputRefs.current[categoryKey] = el}
                    type="file"
                    multiple
                    accept={categoryInfo.extensions.join(',')}
                    onChange={(e) => handleFileSelection(categoryKey, e)}
                    style={{ display: 'none' }}
                    disabled={uploading}
                  />

                  {categoryFiles.length === 0 && !dragOverCategory[categoryKey] && (
                    <div className="resources-drop-hint">Click to select or drag & drop files here</div>
                  )}
                  
                  {categoryFiles.length > 0 && (
                    <div className="resources-selected-compact">
                      {categoryFiles.map((file, index) => (
                        <div key={index} className="resources-file-chip">
                          <span className="resources-chip-name">{file.name}</span>
                          <button onClick={() => handleRemoveFile(categoryKey, index)} disabled={uploading}>×</button>
                        </div>
                      ))}
                      <button onClick={() => handleClearCategory(categoryKey)} className="resources-clear-link" disabled={uploading}>
                        Clear all
                      </button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          
          {getTotalFilesCount() > 0 && (
            <div className="resources-upload-footer">
              <span className="resources-footer-count">{getTotalFilesCount()} file(s) selected</span>
              <button onClick={handleShowConfirmation} className="resources-btn-upload-primary" disabled={uploading}>
                <UploadIcon />
                Upload {getTotalFilesCount()} File(s)
              </button>
            </div>
          )}
        
        {/* Confirmation Modal */}
        {showConfirmation && (
          <div className="resources-confirmation-overlay">
            <div className="resources-confirmation-modal">
              <h4>Confirm Upload</h4>
              <div className="resources-confirmation-summary">
                <strong>{getTotalFilesCount()} files</strong> will be uploaded
              </div>
              
              <div className="resources-confirmation-list">
                {Object.entries(filesByCategory).map(([categoryKey, categoryFiles]) => {
                  if (categoryFiles.length === 0) return null
                  
                  const categoryInfo = CATEGORIES[categoryKey]
                  
                  return (
                    <div key={categoryKey} className="resources-confirmation-category">
                      <div className="resources-confirmation-cat-header">
                        {categoryInfo.icon} {categoryInfo.description} ({categoryFiles.length})
                      </div>
                      <div className="resources-confirmation-files">
                        {categoryFiles.map((file, index) => (
                          <div key={index} className="resources-conf-file">
                            {file.name}
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
              
              <div className="resources-confirmation-actions">
                <button onClick={handleCancelUpload} className="resources-btn-cancel" disabled={uploading}>
                  Cancel
                </button>
                <button onClick={handleConfirmUpload} className="resources-btn-confirm" disabled={uploading}>
                  {uploading ? 'Uploading...' : 'Confirm Upload'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Upload Results */}
        {uploadResults && (
          <div className="resources-results">
            <h5>Results</h5>
            
            {uploadResults.successful.length > 0 && (
              <div className="resources-results-section resources-results-success">
                <div className="resources-results-header">
                  ✅ {uploadResults.successful.length} Successful
                </div>
                <div className="resources-results-list">
                  {uploadResults.successful.map((file, index) => (
                    <div key={index} className="resources-result-item">
                      {file.replaced && <span className="resources-replaced-badge">🔄</span>}
                      <span className="resources-result-category">[{file.category}]</span>
                      <span className="resources-result-filename">{file.filename}</span>
                      <span className="resources-result-size">{formatFileSize(file.size)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {uploadResults.failed.length > 0 && (
              <div className="resources-results-section resources-results-failed">
                <div className="resources-results-header">
                  ❌ {uploadResults.failed.length} Failed
                </div>
                <div className="resources-results-list">
                  {uploadResults.failed.map((file, index) => (
                    <div key={index} className="resources-result-item">
                      <span className="resources-result-filename">{file.filename}</span>
                      <span className="resources-result-error">{file.error}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        </div>
      )}
      
      {/* Map Tab */}
      {activeTab === 'map' && (
        <div className="resources-tab-content">
          <div className="map-manager">
            {/* Status Card */}
            <div className="map-status-card">
              <div className="map-status-header">
                <svg viewBox="0 0 24 24" width="22" height="22">
                  <path d="M3 7l9-4 9 4v10l-9 4-9-4V7z" fill="none" stroke="#d4af37" strokeWidth="1.8" strokeLinejoin="round"/>
                  <path d="M12 3v18M3 7l9 4 9-4" stroke="#d4af37" strokeWidth="1.4" fill="none" strokeLinejoin="round"/>
                </svg>
                <h4>Map Status</h4>
                <div className="map-status-actions">
                  <button
                    className="resources-btn-icon"
                    onClick={handleMapDownload}
                    disabled={mapDownloading || !mapStatus?.mapExists}
                    title={mapStatus?.mapExists ? 'Download map.bin' : 'Map not available'}
                  >
                    <DownloadIcon />
                  </button>
                  <button
                    className="resources-btn-icon"
                    onClick={loadMapStatus}
                    disabled={mapLoading}
                    title="Refresh"
                  >
                    <RefreshIcon />
                  </button>
                </div>
              </div>

              {mapLoading ? (
                <div className="map-status-skeleton">
                  <Skeleton height="20px" borderRadius="6px" />
                  <Skeleton height="20px" borderRadius="6px" />
                  <Skeleton height="20px" borderRadius="6px" />
                </div>
              ) : mapStatus ? (
                <div className="map-status-grid">
                  <div className={`map-status-row ${mapStatus.mapExists ? 'map-status-row--ok' : 'map-status-row--warn'}`}>
                    <span className="map-status-label">File</span>
                    <span className="map-status-value">
                      {mapStatus.mapExists ? '✅ map.bin present' : '⚠️ No map uploaded'}
                    </span>
                  </div>
                  {mapStatus.mapExists && (
                    <>
                      <div className="map-status-row">
                        <span className="map-status-label">Size</span>
                        <span className="map-status-value">{mapStatus.mapSizeFormatted}</span>
                      </div>
                      <div className="map-status-row">
                        <span className="map-status-label">Last modified</span>
                        <span className="map-status-value">
                          {mapStatus.mapLastModified
                            ? new Date(mapStatus.mapLastModified).toLocaleString()
                            : '—'}
                        </span>
                      </div>
                    </>
                  )}
                  <div className={`map-status-row ${mapStatus.tilesInQueue > 0 ? 'map-status-row--warn' : 'map-status-row--ok'}`}>
                    <span className="map-status-label">Tiles in queue</span>
                    <span className="map-status-value">
                      {mapStatus.tilesInQueue}
                      {mapStatus.tilesInQueue > 0 && (
                        <span className="map-queue-size"> ({mapStatus.queueSizeFormatted})</span>
                      )}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="resources-empty">Click refresh to load map status</div>
              )}
            </div>

            {/* Upload Card */}
            <div className="map-upload-card">
              <div className="map-upload-header">
                <UploadIcon />
                <h4>Upload map.bin</h4>
              </div>
              <p className="map-upload-desc">
                Replaces the current authoritative <code>map.bin</code>. Format: raw binary <code>application/octet-stream</code>.
              </p>

              <div
                className={`map-drop-zone ${mapDragOver ? 'map-drop-zone--dragover' : ''}`}
                onClick={() => mapFileInputRef.current?.click()}
                onDragOver={handleMapDragOver}
                onDragLeave={handleMapDragLeave}
                onDrop={handleMapDrop}
              >
                <input
                  ref={mapFileInputRef}
                  type="file"
                  accept=".bin"
                  onChange={handleMapFileSelect}
                  style={{ display: 'none' }}
                  disabled={mapUploading}
                />

                {mapFile ? (
                  <div className="map-selected-file" style={{ margin: 0, background: 'transparent', border: 'none', boxShadow: 'none', padding: 0 }}>
                    <svg viewBox="0 0 24 24" width="16" height="16">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" fill="none" stroke="#d4af37" strokeWidth="1.8"/>
                      <path d="M14 2v6h6" stroke="#d4af37" strokeWidth="1.8" fill="none"/>
                    </svg>
                    <span className="map-selected-name">{mapFile.name}</span>
                    <span className="map-selected-size">{formatFileSize(mapFile.size)}</span>
                    <button
                      className="map-clear-btn"
                      onClick={(e) => { e.stopPropagation(); setMapFile(null); if (mapFileInputRef.current) mapFileInputRef.current.value = '' }}
                      disabled={mapUploading}
                    >×</button>
                  </div>
                ) : (
                  <div className="map-drop-hint">
                    <UploadIcon />
                    <span>Click to select or drag & drop .bin file</span>
                  </div>
                )}
              </div>

              <button
                className="resources-btn-upload-primary"
                onClick={handleMapUpload}
                disabled={!mapFile || mapUploading}
                style={{ marginTop: '1rem' }}
              >
                <UploadIcon />
                {mapUploading ? 'Uploading...' : 'Upload map.bin'}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'files' && (
        <div className="resources-tab-content">
          {loading ? (
            <div className="resources-skeleton">
              <Skeleton height="60px" borderRadius="8px" />
              <Skeleton height="60px" borderRadius="8px" />
              <Skeleton height="60px" borderRadius="8px" />
            </div>
          ) : (
            <div className="resources-categories">
            {Object.entries(CATEGORIES).map(([categoryKey, categoryInfo]) => {
              const categoryFiles = getFilesMap()[categoryKey] || []
              const isExpanded = expandedCategories[categoryKey]
              
              return (
                <div key={categoryKey} className="resources-category">
                  <div className="resources-category-header">
                    <div
                      className="resources-category-title"
                      onClick={() => toggleCategory(categoryKey)}
                    >
                      <span className="resources-category-icon">{categoryInfo.icon}</span>
                      <span className="resources-category-name">{categoryInfo.description}</span>
                      <span className="resources-category-count">({categoryFiles.length})</span>
                    </div>
                    
                    <div className="resources-category-actions">
                      {categoryFiles.length > 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteCategory(categoryKey)
                          }}
                          className="resources-btn resources-btn--delete-category"
                          title={`Delete all ${categoryFiles.length} file(s) from ${categoryKey}`}
                        >
                          <DeleteIcon />
                          Delete All
                        </button>
                      )}
                      <span 
                        className={`resources-category-toggle ${isExpanded ? 'expanded' : ''}`}
                        onClick={() => toggleCategory(categoryKey)}
                      >
                        ▼
                      </span>
                    </div>
                  </div>
                  
                  {isExpanded && (
                    <div className="resources-category-content">
                      {categoryFiles.length === 0 ? (
                        <div className="resources-empty">No files in this category</div>
                      ) : (
                        <div className="resources-files-list">
                          {categoryFiles.map((file, index) => (
                            <div key={index} className="resources-file-item">
                              <div className="resources-file-info">
                                <span className="resources-file-name">{file.filename}</span>
                                <div className="resources-file-meta">
                                  <span className="resources-file-size">{formatFileSize(file.size)}</span>
                                  <span className="resources-divider">•</span>
                                  <span className="resources-file-hash" title={file.hash}>
                                    {formatHash(file.hash)}
                                  </span>
                                </div>
                              </div>
                              <button
                                onClick={() => handleDelete(categoryKey, file.filename)}
                                className="resources-btn resources-btn--delete"
                                title="Delete file"
                              >
                                <DeleteIcon />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ResourcesManager
